import { useHandler } from "@redotech/react-util/hook";
import { RedoAgentKnowledgeRpcClientContext } from "@redotech/redo-merchant-app-common/agent-knowledge-rpc-client-provider";
import { suggestedMerchantKnowledgeIdFormat } from "@redotech/shopper-agent-knowledge-id";
import { sinkPromise } from "@redotech/util/promise";
import {
  createContext,
  Dispatch,
  memo,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

/** A proposal awaiting review. `knowledgeId` is a `smk_` suggestion id. */
export interface KnowledgeSuggestion {
  knowledgeId: string;
  title: string;
  knowledge: string;
}

export interface EditedSuggestionValues {
  response: string;
  expiresAt: Temporal.PlainDate | undefined;
}

export interface KnowledgeCaptureContextValue {
  isOpen: boolean;
  conversationId: string | undefined;
  suggestions: KnowledgeSuggestion[] | undefined;
  /** Short label from the notification, shown before the drafts are opened. */
  preview: string | undefined;
  hideKnowledgeCapture: () => void;
  /**
   * Opens the capture flow for proposals named by id. Ignored while already
   * open, and the drafts are fetched here so the ids can travel on a broadcast.
   */
  showKnowledgeCapture: (input: {
    conversationId: string;
    suggestionIds: readonly string[];
    preview: string;
  }) => void;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  // Modal state that persists across open/close
  editedValues: (EditedSuggestionValues | undefined)[];
  setEditedValues: Dispatch<
    SetStateAction<(EditedSuggestionValues | undefined)[]>
  >;
  currentIndex: number;
  setCurrentIndex: Dispatch<SetStateAction<number>>;
  resolvedSuggestionIndices: Set<number>;
  setResolvedSuggestionIndices: Dispatch<SetStateAction<Set<number>>>;
}

export const KnowledgeCaptureContext = createContext<
  KnowledgeCaptureContextValue | undefined
>(undefined);

KnowledgeCaptureContext.displayName = "KnowledgeCaptureContext";

export interface KnowledgeCaptureProviderProps {
  children: ReactNode;
}

export const KnowledgeCaptureProvider = memo(function KnowledgeCaptureProvider({
  children,
}: KnowledgeCaptureProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [conversationId, setConversationId] = useState<string | undefined>(
    undefined,
  );
  const [suggestions, setSuggestions] = useState<
    KnowledgeSuggestion[] | undefined
  >(undefined);
  const [preview, setPreview] = useState<string | undefined>(undefined);

  // Modal state that persists across open/close
  const [editedValues, setEditedValues] = useState<
    (EditedSuggestionValues | undefined)[]
  >([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [resolvedSuggestionIndices, setResolvedSuggestionIndices] = useState<
    Set<number>
  >(new Set());

  const agentKnowledgeRpcClient = useContext(
    RedoAgentKnowledgeRpcClientContext,
  );

  const isOpenRef = useRef(isOpen);
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  const hideKnowledgeCapture = useHandler(() => {
    setIsOpen(false);
    setConversationId(undefined);
    setSuggestions(undefined);
    setPreview(undefined);
    // Reset modal state for next session
    setEditedValues([]);
    setCurrentIndex(0);
    setResolvedSuggestionIndices(new Set());
  });

  const showKnowledgeCapture = useHandler(
    (input: {
      conversationId: string;
      suggestionIds: readonly string[];
      preview: string;
    }) => {
      // Ignore while open so a second ticket cannot discard suggestions the
      // merchant is part-way through reviewing.
      if (isOpenRef.current || input.suggestionIds.length === 0) {
        return;
      }
      const ids = input.suggestionIds.flatMap((id) => {
        const parsed = suggestedMerchantKnowledgeIdFormat.schema.safeParse(id);
        return parsed.success ? [parsed.data] : [];
      });
      if (!agentKnowledgeRpcClient || ids.length === 0) {
        return;
      }
      sinkPromise(
        (async () => {
          const fetched = await agentKnowledgeRpcClient.getKnowledgeSuggestions(
            { ids },
          );
          // Opened between the notification and the fetch, or every proposal
          // was reviewed already — either way, do not interrupt.
          if (isOpenRef.current || fetched.length === 0) {
            return;
          }
          setConversationId(input.conversationId);
          setPreview(input.preview);
          setSuggestions(
            fetched.map((suggestion) => ({
              knowledgeId: suggestion.id,
              title: suggestion.title,
              knowledge: suggestion.content,
            })),
          );
          setIsOpen(true);
        })(),
      );
    },
  );

  return (
    <KnowledgeCaptureContext.Provider
      value={{
        isOpen,
        conversationId,
        suggestions,
        preview,
        hideKnowledgeCapture,
        showKnowledgeCapture,
        setIsOpen,
        editedValues,
        setEditedValues,
        currentIndex,
        setCurrentIndex,
        resolvedSuggestionIndices,
        setResolvedSuggestionIndices,
      }}
    >
      {children}
    </KnowledgeCaptureContext.Provider>
  );
});
